import { useEffect, useState } from 'react';
import { Banknote, Check } from 'lucide-react';
import ModalShell from '../../components/ui/ModalShell';
import Dropdown from '../../components/receptura/Dropdown';
import { csrf } from './spolecne';

// fmtKc ze spolecne.js vrací pro nulu prázdný řetězec — tady má být „0 Kč“.
const fmtKc = (n) => `${Math.round(Number(n) || 0).toLocaleString('cs')} Kč`;

/**
 * Oprava vyplacené směny — pro vedení.
 *
 * Když se směna zapíše se špatným úvazkem (pomocná směna zaměstnance na fixu
 * bez souběžné DPP → HPP → k výplatě 0), peníze se vyplatí ručně z pokladny
 * a docházka pak neodpovídá. Tady se srovná: úvazek, záloha a navázání ruční
 * výplaty, ať ji mzdy nepočítají znovu. Pokladní pohyb nevzniká — peníze už
 * odešly. Důvod je povinný a zapíše se do poznámky docházky.
 */
export default function OpravaVyplatyModal({ dochazkaId, isDark, barva, onClose, onUlozeno }) {
    const [d, setD] = useState(null);
    const [chyba, setChyba] = useState(null);
    const [typ, setTyp] = useState('');
    const [castka, setCastka] = useState('');
    const [navazat, setNavazat] = useState([]);
    const [duvod, setDuvod] = useState('');
    const [ukladam, setUkladam] = useState(false);

    useEffect(() => {
        fetch(`/api/dochazka/${dochazkaId}/oprava-vyplaty`, { headers: { Accept: 'application/json' } })
            .then(r => r.ok ? r.json() : r.json().then(x => Promise.reject(x)))
            .then(x => {
                setD(x);
                // Návrh: pomocná směna fixaře se souběžnou DPP → DPP; částka z hodin × sazby
                setTyp(x.typ_uvazku === 'HPP' && x.soubezna_dpp && x.sazba_kc_h ? 'DPP' : (x.typ_uvazku || 'HPP'));
                setCastka(x.castka_zaloha_kc ?? x.navrh_zaloha_kc ?? '');
                setNavazat(x.kandidati.length === 1 ? [x.kandidati[0].id] : []);
            })
            .catch(e => setChyba(e?.error || e?.message || 'Nepodařilo se načíst.'));
    }, [dochazkaId]);

    const lze = d && typ && duvod.trim().length >= 5 && !ukladam;
    const vyplaceno = d ? [...d.navazane_pohyby, ...d.kandidati.filter(k => navazat.includes(k.id))].reduce((s, p) => s + p.castka, 0) : 0;
    const rozdil = castka !== '' ? vyplaceno - Number(castka) : null;

    const ulozit = async () => {
        if (!lze) return;
        setUkladam(true);
        setChyba(null);
        try {
            const res = await fetch(`/api/dochazka/${dochazkaId}/oprava-vyplaty`, {
                method: 'POST',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json', 'X-CSRF-TOKEN': csrf() },
                body: JSON.stringify({
                    typ_uvazku_kod: typ,
                    castka_zaloha_kc: castka === '' ? null : Number(castka),
                    navazat_pohyb_ids: navazat,
                    duvod: duvod.trim(),
                }),
            });
            const x = await res.json().catch(() => ({}));
            if (!res.ok) { setChyba(x.error || (x.errors && Object.values(x.errors)[0]?.[0]) || 'Uložení selhalo.'); return; }
            onUlozeno?.();
        } finally {
            setUkladam(false);
        }
    };

    const hrana = isDark ? 'rgba(255,255,255,0.14)' : 'var(--border-default)';
    const pole = { width: '100%', height: 40, borderRadius: 8, padding: '0 12px', fontSize: 14, outline: 'none', background: isDark ? 'rgba(0,0,0,0.3)' : 'var(--surface-raised)', border: `1px solid ${hrana}`, color: 'var(--text-primary)' };
    const popisek = { fontSize: 11, fontWeight: 700, letterSpacing: '0.05em', textTransform: 'uppercase', color: 'var(--text-muted)', margin: '14px 0 6px' };
    const radek = (l, h) => (
        <div style={{ display: 'flex', justifyContent: 'space-between', gap: 12, fontSize: 13, padding: '6px 0', borderBottom: `1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'var(--border-subtle)'}` }}>
            <span style={{ color: 'var(--text-muted)' }}>{l}</span><span style={{ color: 'var(--text-primary)', fontWeight: 600 }}>{h}</span>
        </div>
    );
    const kdy = (iso) => iso ? new Date(iso).toLocaleString('cs-CZ', { day: 'numeric', month: 'numeric', hour: '2-digit', minute: '2-digit' }) : '';

    return (
        <ModalShell onClose={onClose} zavritLze={!ukladam} isDark={isDark} sirka={500} ikona={Banknote} barva={barva}
            nadpis={d ? `Oprava výplaty — ${d.jmeno}` : 'Oprava výplaty'}
            paticka={(
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    {chyba && <span style={{ flex: 1, fontSize: 12, color: '#EF4444' }}>{chyba}</span>}
                    <button type="button" onClick={onClose} disabled={ukladam} style={{ marginLeft: 'auto', padding: '9px 14px', borderRadius: 8, background: 'transparent', border: `1px solid ${hrana}`, color: 'var(--text-secondary)', fontSize: 13, cursor: 'pointer' }}>Zrušit</button>
                    <button type="button" onClick={ulozit} disabled={!lze} style={{ padding: '9px 16px', borderRadius: 8, background: barva, border: 'none', color: '#fff', fontSize: 13, fontWeight: 600, cursor: lze ? 'pointer' : 'not-allowed', opacity: lze ? 1 : 0.5 }}>
                        {ukladam ? 'Ukládám…' : 'Uložit opravu'}
                    </button>
                </div>
            )}>
            <div style={{ padding: '4px 16px 16px' }}>
                {!d ? (
                    <p style={{ fontSize: 13, color: 'var(--text-muted)' }}>{chyba || 'Načítám…'}</p>
                ) : (
                    <>
                        <p style={{ fontSize: 12.5, lineHeight: 1.5, color: 'var(--text-secondary)', margin: '8px 0 6px' }}>
                            Srovná docházku s tím, co se opravdu vyplatilo. Do pokladny se nic nezapisuje — peníze už odešly.
                        </p>
                        {radek('Směna', new Date(d.datum + 'T12:00:00').toLocaleDateString('cs-CZ', { weekday: 'long', day: 'numeric', month: 'numeric' }))}
                        {radek('Odpracováno', d.hodiny !== null ? `${String(d.hodiny).replace('.', ',')} h × ${d.sazba_kc_h ? `${d.sazba_kc_h} Kč/h` : 'bez sazby'}` : '—')}
                        {radek('Úvazek v docházce / na kartě', `${d.typ_uvazku || '—'} / ${d.typ_uvazku_karta || '—'}${d.soubezna_dpp ? ' + souběžná DPP' : ''}`)}
                        {radek('Záloha v docházce', fmtKc(d.castka_zaloha_kc || 0))}

                        <p style={popisek}>Úvazek pro tuhle směnu</p>
                        <Dropdown value={typ} onChange={setTyp} options={d.typy.map(t => ({ value: t, label: t }))} isDark={isDark} accent={barva} height={40} />

                        <p style={popisek}>Záloha (Kč){d.navrh_zaloha_kc ? ` · podle hodin ${fmtKc(d.navrh_zaloha_kc)}` : ''}</p>
                        <input type="number" inputMode="numeric" min="0" value={castka} onChange={e => setCastka(e.target.value)} style={{ ...pole, maxWidth: 200 }} />

                        <p style={popisek}>Ruční výplata z pokladny</p>
                        {d.navazane_pohyby.map(p => (
                            <div key={p.id} style={{ fontSize: 13, color: 'var(--text-secondary)', padding: '4px 0' }}>Už navázáno: #{p.id} · {fmtKc(p.castka)} · {kdy(p.kdy)}</div>
                        ))}
                        {d.kandidati.length === 0 && d.navazane_pohyby.length === 0 && (
                            <p style={{ fontSize: 12.5, color: 'var(--text-muted)', margin: 0 }}>Kolem data směny není žádná nenavázaná záloha tohoto zaměstnance.</p>
                        )}
                        {d.kandidati.map(p => {
                            const on = navazat.includes(p.id);
                            return (
                                <button key={p.id} type="button" onClick={() => setNavazat(prev => on ? prev.filter(x => x !== p.id) : [...prev, p.id])}
                                    style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', minHeight: 48, padding: '8px 12px', marginTop: 6, borderRadius: 8, border: `1px solid ${on ? barva : hrana}`, background: 'transparent', cursor: 'pointer', textAlign: 'left' }}>
                                    <span style={{ width: 20, height: 20, borderRadius: 5, border: `1.5px solid ${on ? barva : hrana}`, background: on ? barva : 'transparent', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>{on && <Check size={14} color="#fff" />}</span>
                                    <span style={{ flex: 1, minWidth: 0 }}>
                                        <span style={{ display: 'block', fontSize: 13.5, fontWeight: 600, color: 'var(--text-primary)' }}>Záloha {fmtKc(p.castka)} · {kdy(p.kdy)}</span>
                                        <span style={{ display: 'block', fontSize: 11.5, color: 'var(--text-muted)' }}>pohyb #{p.id}{p.poznamka ? ` · ${p.poznamka}` : ''}</span>
                                    </span>
                                </button>
                            );
                        })}
                        {rozdil !== null && vyplaceno > 0 && Math.abs(rozdil) >= 1 && (
                            <p style={{ fontSize: 12.5, color: '#F59E0B', margin: '8px 0 0' }}>
                                Vyplaceno {fmtKc(vyplaceno)}, záloha {fmtKc(Number(castka))} — {rozdil > 0 ? `přeplatek ${fmtKc(rozdil)}` : `nedoplatek ${fmtKc(-rozdil)}`}.
                            </p>
                        )}

                        <p style={popisek}>Důvod</p>
                        <textarea value={duvod} onChange={e => setDuvod(e.target.value)} rows={2}
                            placeholder="Např. pomocná směna jako souběžná DPP, chyběla na kartě; vyplaceno ručně."
                            style={{ ...pole, height: 'auto', padding: '8px 12px', resize: 'vertical' }} />
                    </>
                )}
            </div>
        </ModalShell>
    );
}
